import React from 'react';
import { useTranslation } from 'react-i18next';
import { ALERT_BANNER_DURATION_DEPLOY_SUCCESS, handleOnClickEvent, handleKeyEvent } from 'Utils';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getUser } from 'Selectors';
import { Button } from '@brighterion/br-ui';
import { NumberService } from 'Services';
import useIsDeploying from 'Hooks/useIsDeploying';
import { DEPLOYMENT_STATUS } from 'Common/enums';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import DeployModal from './DeployModal';

import styles from './styles.css';

type DeployButtonProps = {
    rulesType?: RulesType;
};

const DeployButton: React.FC<DeployButtonProps> = ({ rulesType }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { email, userId } = useSelector(getUser);
    const { addAlert } = useAlertBanner();
    const { addModal, pop } = useReduxModal();
    const [isOpen, setIsOpen] = React.useState(false);
    const onSuccess = (data?: DeploymentProcess) => {
        const isComplete = data?.status === DEPLOYMENT_STATUS.COMPLETE;
        // should only show alert for the user that created the deployment
        if (isComplete && (email === data?.createdBy || userId === data?.createdBy) && !data?.details?.simulationId) {
            const latestVersion = NumberService.getLocaleNumber(data?.details?.deployedVersion ?? 0);
            addAlert({
                id: 'deploy-success',
                content: t('Version {{ version }} deployment is now live', {
                    version: latestVersion
                }),
                status: 'success',
                duration: ALERT_BANNER_DURATION_DEPLOY_SUCCESS
            });
        }
        // deploying invalidates a lot of cached data.
        // it's better to invalidate everything than track and maintain a list of resources to be refetched
        queryClient.invalidateQueries();
    };
    const onError = (err: any) => {
        pop('deploy-success');
        addModal({
            id: 'deploy-error',
            status: 'error',
            title: t('Deployment failed'),
            content: (
                <>
                    <>{t('The deployment failed try again')}</>
                    {err && err.id && <div className={styles.processId}>Process ID: {err.id}</div>}
                </>
            ),
            cancelText: t('Close')
        });
    };
    const isDeploying = useIsDeploying(false, onSuccess, onError);
    const toggleModal = () => {
        setIsOpen(!isOpen);
    };

    return (
        <>
            <Button
                dataTestId='deploy-btn'
                variant='primary'
                size='small'
                onClick={handleOnClickEvent(toggleModal)}
                onKeyDown={handleKeyEvent(toggleModal)}
                disabled={isDeploying}
            >
                {isDeploying ? t('Deployment In Progress') : t('Deploy')}
            </Button>
            <DeployModal isOpen={isOpen} closeModal={() => setIsOpen(false)} rulesType={rulesType} />
        </>
    );
};

export default DeployButton;
